import React from 'react';
import { cn } from '@/helpers/utils';
import { IconChevronRight } from '@americanexpress/dls-icons';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { ChefProfile, Profile } from '../interface';
import styles from './Partner.module.scss';

interface PartnerProps {
  partner: ChefProfile | Profile;
  anchorId?: string;
  trackingRole: string;
  showMobileView?: boolean;
}

const Partner: React.FC<PartnerProps> = ({
  partner,
  anchorId,
  trackingRole,
  showMobileView = false,
}) => {
  const { customTrack, identifier } = useAnalyticsContext();

  const {
    bio,
    exampleImage,
    exampleImageAltText,
    exampleImageFootnote,
    name,
    profileImage,
    profileImageAltText,
    profileImageFootnote,
    quote,
    resyButtonText,
    resyButtonUrl,
    signatureImage,
    signatureImageAltText,
  } = partner;

  const handleResyClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    customTrack({
      event,
      identifier: `${identifier}`,
      element: `${trackingRole}>>Explore on Resy`,
      detail: name,
    });

    const openedWindow = window.open(
      resyButtonUrl,
      '_blank',
      'noopener,noreferrer'
    );

    if (openedWindow) {
      openedWindow.opener = null;
    }
  };

  return (
    <div
      className={cn('tw-col-span-8 tw-grid tw-grid-cols-1 tw-gap-y-10', {
        'md_tw-gap-y-16 lg_tw-grid-cols-8': !showMobileView,
      })}
      id={anchorId}
    >
      <div className="flex tw-col-span-6 tw-col-start-2 tw-flex-col tw-gap-y-2">
        <div className="color-text-emphasis tw-font-sans tw-font-bold">
          {name}
        </div>
        <div dangerouslySetInnerHTML={{ __html: bio }} />
        <div className="margin-1-b">
          <button
            type="button"
            className={cn(
              'tw-group tw-inline-flex tw-items-center tw-gap-1 tw-border-0 tw-bg-transparent tw-py-2dot5 tw-pr-0dot5 tw-font-sans tw-text-base tw-font-medium tw-text-blue-bright-shade-600-s8 tw-decoration-blue-bright-shade-600-s8 hover_tw-underline focus_tw-underline',
              styles.resyButton
            )}
            onClick={handleResyClick}
          >
            {`${resyButtonText} Resy`}
            <IconChevronRight
              size="xs"
              isFilled={true}
              className="tw-transform tw-text-blue-bright-shade-600-s8 tw-transition-all tw-duration-200 group-hover_tw-translate-x-0dot5"
            />
          </button>
        </div>
      </div>

      <div
        className={cn('tw-col-span-8 tw-grid tw-gap-y-10', {
          'md_tw-gap-y-16': !showMobileView,
        })}
      >
        <div className="flex tw-flex-wrap tw-gap-y-10 md_tw-flex-nowrap md_tw-gap-x-6">
          <div className="md_tw-flex-1">
            <img
              className="tw-rounded-2xl"
              src={profileImage}
              alt={profileImageAltText}
            />
            {profileImageFootnote && (
              <div className="text-align-center margin-2-t color-text-subtle">
                {profileImageFootnote}
              </div>
            )}
          </div>
          <div className="md_tw-flex-1">
            <img
              className="tw-rounded-2xl"
              src={exampleImage}
              alt={exampleImageAltText}
            />
            {exampleImageFootnote && (
              <div className="text-align-center margin-2-t color-text-subtle">
                {exampleImageFootnote}
              </div>
            )}
          </div>
        </div>
        <div
          className={cn(
            'text-align-center color-text-subtle tw-font-serif tw-text-2xl',
            {
              'md_tw-text-5xl': !showMobileView,
            }
          )}
        >
          <p>&ldquo;{quote}&rdquo;</p>
        </div>
        <div className={cn('flex tw-justify-center', styles.signature)}>
          <img
            className="tw-object-contain"
            src={signatureImage}
            alt={signatureImageAltText}
          />
        </div>
      </div>
    </div>
  );
};

export default Partner;
